/* eslint-disable max-lines */
import React, { useState } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  FileDownload as FileDownloadIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  TipsAndUpdates as TipsIcon,
  Build as BuildIcon,
  Assessment as AssessmentIcon,
  Group as GroupIcon,
  Description as DescriptionIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import {} from '@mui/material/styles';

// eslint-disable-next-line max-lines-per-function
function CSVImportInstructions({ onDownloadTemplate }) {
  const [expandedPanel, setExpandedPanel] = useState('getting-started');

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  const downloadTemplate = () => {
    // Use the comprehensive template from sampleCSVGenerator
    const template = `Name,First Name,Last Name,Email,Phone,Organization,Role,Department,LinkedIn URL,Bio,Tags,Skills,Courage Score,Vulnerability Score,Purpose Driven Score,Learning Agility Score,Determination Score,Self Awareness Score,Adaptability Score,Cross Cultural Score,Holistic Health Score,Financial Fitness Score,Community Engagement Score,Leadership Strength Score,Growth Trajectory,Growth Velocity,Assessment Date,Coach Name,Growth Risk Index,Risk Factors
Sarah Chen,Sarah,Chen,sarah.chen@techcorp.com,(555) 234-5678,TechCorp Industries,Chief Technology Officer,Technology,https://linkedin.com/in/sarachen,"Visionary technology leader with 15 years of experience in AI and machine learning.","leadership,ai,innovation","Machine Learning,Strategic Planning,Public Speaking",2.9,2.7,3.0,2.8,2.9,2.8,3.0,2.9,88,82,94,91,Advanced,Rapid,2024-01-15,Michael Torres,2.2,talent_retention
Marcus Johnson,Marcus,Johnson,mjohnson@globalsolutions.com,(555) 345-6789,Global Solutions LLC,Senior Vice President,Operations,https://linkedin.com/in/marcusjohnson,"Operations executive specializing in process optimization.","operations,transformation","Process Optimization,Change Management,Lean Six Sigma",2.6,2.4,2.7,2.5,2.8,2.5,2.6,2.3,79,85,81,83,Developing,Moderate,2024-02-20,Amanda Williams,2.8,"process_complexity,regulatory_changes"`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'faana_person_import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    if (onDownloadTemplate) {
      onDownloadTemplate();
    }
  };

  const fieldMappings = [
    // Basic Information Fields
    { field: 'name', description: 'Full name of the person', required: true, type: 'Text', category: 'Basic' },
    { field: 'firstName', description: 'First name of the person', required: false, type: 'Text', category: 'Basic' },
    { field: 'lastName', description: 'Last name of the person', required: false, type: 'Text', category: 'Basic' },
    { field: 'email', description: 'Primary email address (must be unique)', required: true, type: 'Email', category: 'Basic' },
    { field: 'phone', description: 'Phone number in any format', required: false, type: 'Text', category: 'Basic' },
    { field: 'organization', description: 'Company or organization name', required: false, type: 'Text', category: 'Basic' },
    { field: 'role', description: 'Job title or role', required: false, type: 'Text', category: 'Basic' },
    { field: 'department', description: 'Department or division within organization', required: false, type: 'Text', category: 'Basic' },
    { field: 'linkedinUrl', description: 'LinkedIn profile URL', required: false, type: 'URL', category: 'Basic' },
    { field: 'bio', description: 'Short biography or description', required: false, type: 'Text', category: 'Basic' },
    { field: 'tags', description: 'Comma-separated list of tags for categorization', required: false, type: 'Text', category: 'Basic' },
    { field: 'skills', description: 'Comma-separated list of skills', required: false, type: 'Text', category: 'Basic' },

    // Leadership Quality Scores (1-3 scale) - Faana Methodology
    { field: 'courage_score', description: 'Ability to face challenges and take calculated risks (1.0-3.0)', required: false, type: 'Number (1.0-3.0)', category: 'Leadership' },
    { field: 'vulnerability_score', description: 'Openness to feedback and admitting weaknesses (1.0-3.0)', required: false, type: 'Number (1.0-3.0)', category: 'Leadership' },
    { field: 'purpose_driven_score', description: 'Alignment with mission beyond personal success (1.0-3.0)', required: false, type: 'Number (1.0-3.0)', category: 'Leadership' },
    { field: 'learning_agility_score', description: 'Speed of acquiring and applying new knowledge (1.0-3.0)', required: false, type: 'Number (1.0-3.0)', category: 'Leadership' },
    { field: 'determination_score', description: 'Persistence in pursuing goals despite obstacles (1.0-3.0)', required: false, type: 'Number (1.0-3.0)', category: 'Leadership' },
    { field: 'self_awareness_score', description: 'Understanding of personal impact on others (1.0-3.0)', required: false, type: 'Number (1.0-3.0)', category: 'Leadership' },
    { field: 'adaptability_score', description: 'Flexibility in changing circumstances (1.0-3.0)', required: false, type: 'Number (1.0-3.0)', category: 'Leadership' },
    { field: 'cross_cultural_score', description: 'Effectiveness across cultural boundaries (1.0-3.0)', required: false, type: 'Number (1.0-3.0)', category: 'Leadership' },

    // Framework Scores (0-100 scale) - Faana Methodology
    { field: 'holistic_health_score', description: 'Physical, mental, and spiritual wellness (0-100)', required: false, type: 'Number (0-100)', category: 'Framework' },
    { field: 'financial_fitness_score', description: 'Financial acumen and resource management (0-100)', required: false, type: 'Number (0-100)', category: 'Framework' },
    { field: 'community_engagement_score', description: 'Network strength and contribution (0-100)', required: false, type: 'Number (0-100)', category: 'Framework' },
    { field: 'leadership_strength_score', description: 'Overall leadership effectiveness (0-100)', required: false, type: 'Number (0-100)', category: 'Framework' },

    // Growth Metrics - Faana Methodology
    { field: 'growth_trajectory', description: 'Current development phase: Emerging, Developing, Advanced, or Master', required: false, type: 'Text', category: 'Growth' },
    { field: 'growth_velocity', description: 'Rate of development: Slow, Moderate, or Rapid', required: false, type: 'Text', category: 'Growth' },
    { field: 'assessment_date', description: 'Date of last formal assessment (YYYY-MM-DD format)', required: false, type: 'Date', category: 'Growth' },
    { field: 'coach_name', description: 'Faana-certified coach conducting assessment', required: false, type: 'Text', category: 'Growth' },

    // Risk Assessment - Faana Methodology
    { field: 'growth_risk_index', description: 'Overall risk to growth (0-10, where 10 is highest risk)', required: false, type: 'Number (0-10)', category: 'Risk' },
    { field: 'risk_factors', description: 'Comma-separated list of identified growth barriers', required: false, type: 'Text', category: 'Risk' },

    // Additional Fields
    { field: 'notes', description: 'Additional notes or comments', required: false, type: 'Text', category: 'Basic' }
  ];

  const commonErrors = [
    { error: 'Invalid email format', solution: 'Use valid email addresses (user@domain.com)' },
    { error: 'Duplicate email addresses', solution: 'Each email must be unique in your file' },
    { error: 'Score out of range', solution: 'Leadership: 1-3, Framework scores: 0-100' },
    { error: 'File too large', solution: 'Maximum 10MB file size, 10,000 rows' },
    { error: 'Encoding issues', solution: 'Save as UTF-8 encoded CSV' },
    { error: 'Commas in data', solution: 'Wrap text with commas in double quotes' }
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Quick Start:</strong> Download the template below, fill it with your data, and upload it to get started.
          All steps include helpful validation and guidance.
        </Typography>
      </Alert>
      <Accordion
        expanded={expandedPanel === 'getting-started'}
        onChange={handleAccordionChange('getting-started')}
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="primary" />
            <Typography variant="h6">Getting Started</Typography>
            <Chip label="Start Here" color="primary" size="small" />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="body1" paragraph>
              Follow these simple steps to import your people data:
            </Typography>

            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={downloadTemplate}
              size="large"
              sx={{ mb: 3, mr: 2 }}
            >
              Download CSV Template
            </Button>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 3 }}>
              Comprehensive Faana template with all 31 supported fields including leadership scores, framework assessments, and growth metrics
            </Typography>

            <List>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                <ListItemText
                  primary="Prepare Your Data"
                  secondary="Fill the template with your people information, keeping required fields complete"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                <ListItemText
                  primary="Upload CSV File"
                  secondary="Drag and drop or browse to select your completed CSV file"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                <ListItemText
                  primary="Map Fields"
                  secondary="Review and adjust how your CSV columns match our system fields"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                <ListItemText
                  primary="Handle Duplicates"
                  secondary="Review any potential duplicate entries and choose how to handle them"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                <ListItemText
                  primary="Import & Review"
                  secondary="Monitor the import process and review the results"
                />
              </ListItem>
            </List>
          </Box>
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expandedPanel === 'formatting'}
        onChange={handleAccordionChange('formatting')}
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon color="primary" />
            <Typography variant="h6">CSV Formatting Guidelines</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Typography variant="subtitle2" gutterBottom color="primary">
                File Requirements
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="UTF-8 encoding recommended" secondary="Prevents character display issues" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Comma-separated values (.csv)" secondary="Standard CSV format" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Headers in first row" secondary="Column names must be in row 1" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Maximum 10MB file size" secondary="Up to 10,000 rows supported" />
                </ListItem>
              </List>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Typography variant="subtitle2" gutterBottom color="primary">
                Data Formatting
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="Text with commas" secondary='Wrap in quotes: "Smith, John Jr."' />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Email addresses" secondary="Must be valid format: user@domain.com" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Phone numbers" secondary="Any format accepted: (555) 123-4567" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="URLs" secondary="Include https:// for web addresses" />
                </ListItem>
              </List>
            </Grid>
          </Grid>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Special Characters:</strong> If your data contains commas, quotes, or line breaks,
              wrap the entire field in double quotes and escape internal quotes by doubling them.
            </Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expandedPanel === 'fields'}
        onChange={handleAccordionChange('fields')}
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupIcon color="primary" />
            <Typography variant="h6">Field Descriptions</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {/* Group fields by category */}
          {['Basic', 'Leadership', 'Framework', 'Growth', 'Risk'].map((category) => {
            const categoryFields = fieldMappings.filter(field => field.category === category);
            if (categoryFields.length === 0) return null;

            return (
              <Box key={category} sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ mt: category !== 'Basic' ? 2 : 0 }}>
                  {category === 'Basic' && 'Basic Information Fields'}
                  {category === 'Leadership' && 'Leadership Quality Scores (Faana Methodology)'}
                  {category === 'Framework' && 'Framework Scores (Faana Methodology)'}
                  {category === 'Growth' && 'Growth Metrics (Faana Methodology)'}
                  {category === 'Risk' && 'Risk Assessment (Faana Methodology)'}
                </Typography>
                {category === 'Leadership' && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Leadership Quality Scores:</strong> Based on Faana Leadership Assessment Framework v2.1.
                      Neuroscience-based assessments measuring prefrontal cortex activation patterns.
                    </Typography>
                  </Alert>
                )}
                {category === 'Framework' && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Framework Scores:</strong> Comprehensive assessment frameworks measuring
                      holistic wellness, financial fitness, community impact, and overall leadership strength.
                    </Typography>
                  </Alert>
                )}
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Field Name</strong></TableCell>
                        <TableCell><strong>Description</strong></TableCell>
                        <TableCell><strong>Required</strong></TableCell>
                        <TableCell><strong>Type/Range</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {categoryFields.map((field) => (
                        <TableRow key={field.field}>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {field.field}
                            </Typography>
                          </TableCell>
                          <TableCell>{field.description}</TableCell>
                          <TableCell>
                            {field.required ? (
                              <Chip label="Required" color="error" size="small" />
                            ) : (
                              <Chip label="Optional" color="default" size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {field.type}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })}

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom color="primary">
              Faana Methodology Score Explanations
            </Typography>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Leadership Quality Scores (1.0-3.0):</strong><br />
                    • 1.0-1.9 = Emerging/Developing<br />
                    • 2.0-2.4 = Proficient<br />
                    • 2.5-2.9 = Advanced<br />
                    • 3.0 = Mastery Level
                  </Typography>
                </Alert>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Framework Scores (0-100):</strong><br />
                    • 0-39 = Needs Development<br />
                    • 40-69 = Developing<br />
                    • 70-84 = Proficient<br />
                    • 85-100 = Advanced/Exceptional
                  </Typography>
                </Alert>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Alert severity="warning">
                  <Typography variant="body2">
                    <strong>Growth Risk Index (0-10):</strong><br />
                    • 0-2 = Low Risk<br />
                    • 3-5 = Moderate Risk<br />
                    • 6-8 = High Risk<br />
                    • 9-10 = Critical Risk
                  </Typography>
                </Alert>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Alert severity="success">
                  <Typography variant="body2">
                    <strong>Growth Trajectory:</strong><br />
                    Emerging → Developing → Advanced → Master<br />
                    <strong>Growth Velocity:</strong><br />
                    Slow → Moderate → Rapid
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expandedPanel === 'process'}
        onChange={handleAccordionChange('process')}
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon color="primary" />
            <Typography variant="h6">Import Process Details</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Typography variant="subtitle2" gutterBottom color="primary">
                Step 1: File Upload
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Drag and drop or click to browse" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Automatic file validation" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Preview of first few rows" />
                </ListItem>
              </List>

              <Typography variant="subtitle2" gutterBottom color="primary" sx={{ mt: 2 }}>
                Step 2: Field Mapping
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Automatic field detection" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Manual mapping adjustment" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Data preview with mappings" />
                </ListItem>
              </List>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Typography variant="subtitle2" gutterBottom color="primary">
                Step 3: Duplicate Handling
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Automatic duplicate detection" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Choose: Create new, Update existing, or Skip" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Side-by-side comparison view" />
                </ListItem>
              </List>

              <Typography variant="subtitle2" gutterBottom color="primary" sx={{ mt: 2 }}>
                Step 4: Import & Results
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Real-time progress tracking" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Detailed success/error reporting" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Option to download results report" />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expandedPanel === 'tips'}
        onChange={handleAccordionChange('tips')}
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TipsIcon color="primary" />
            <Typography variant="h6">Tips for Success</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Best Practices</Typography>
                <List dense>
                  <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="Start with a small test file (10-20 rows)" />
                  </ListItem>
                  <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="Ensure email addresses are unique and valid" />
                  </ListItem>
                  <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="Use consistent organization naming" />
                  </ListItem>
                  <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="Keep scores within valid ranges" />
                  </ListItem>
                  <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="Review duplicate suggestions carefully" />
                  </ListItem>
                </List>
              </Alert>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Performance Tips</Typography>
                <List dense>
                  <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="Import during off-peak hours for large files" />
                  </ListItem>
                  <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="Split very large datasets into smaller chunks" />
                  </ListItem>
                  <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="Clean data before import (remove empty rows)" />
                  </ListItem>
                  <ListItem sx={{ py: 0 }}>
                    <ListItemText primary="Use template format to avoid mapping issues" />
                  </ListItem>
                </List>
              </Alert>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expandedPanel === 'troubleshooting'}
        onChange={handleAccordionChange('troubleshooting')}
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BuildIcon color="primary" />
            <Typography variant="h6">Troubleshooting</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="subtitle2" gutterBottom color="primary">
            Common Issues and Solutions
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Problem</strong></TableCell>
                  <TableCell><strong>Solution</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {commonErrors.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ErrorIcon color="error" fontSize="small" />
                        {item.error}
                      </Box>
                    </TableCell>
                    <TableCell>{item.solution}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Import Limits:</strong> Maximum 10MB file size and 10,000 rows per import.
              For larger datasets, split your data into multiple files and import them separately.
            </Typography>
          </Alert>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Duplicate Detection:</strong> The system matches on email addresses and similar names.
              Review all suggested duplicates carefully before proceeding with the import.
            </Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default CSVImportInstructions;